import {prepareOfferDocumentNumber} from '../../lib/utils/offerDocumentNumber';
import {useFluPilot} from '../../lib/onboarding/consultant/useFluPilot';
import {FluRecordTrigger} from '../../components/onboarding/FluRecordTrigger';
import React, { useEffect, useState, useMemo, useRef, Component, ErrorInfo, ReactNode, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { PageContainer, Modal, PDFViewerModal } from '../../components/ui';
import { ResponsiveModal } from '../../components/ui/ResponsiveModal';
import { MinimalLoader } from '../../components/ui/MinimalLoader';
import { useDealStore } from '../../lib/stores/dealStore';
import { askDealLinkedOrders } from '../../lib/services/dealDeleteOrders';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { useSignatureStore } from '../../lib/stores/signatureStore';
import { useClientStore } from '../../lib/stores/clientStore';
import { useTemplatesForDeal } from '../../lib/hooks/useTemplatesForDeal';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useContractClientGate } from '../../hooks/useContractClientGate';
import { useAiHelpStore } from '../../lib/stores/aiHelpStore';
import { useApp } from '../../components/AppProvider';
import { dealsTranslations } from '../../lib/i18n/translations/deals';
import { useAuth, useUserData } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { getFreshContractUrl } from '../../lib/utils/contractUrl';
import { ContractRenewalSection } from '../../components/deals/ContractRenewalSection';
import DocumentCollectionsSection from '../../components/documents/DocumentCollectionsSection';

// Define ErrorBoundary component for handling component errors
interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }

    return this.props.children;
  }
}

// Import the components
import DealClientInfo from '../../components/deals/DealClientInfo';
import DealServicesSection from '../../components/deals/DealServicesSection';
import DealOrderSection from '../../components/deals/DealOrderSection';
import { DealCostsSection } from '../../components/deals/DealCostsSection';
import DealNotesSection from '../../components/deals/DealNotesSection';
import { toComposerDateInputValue, toComposerTimeInputValue } from '../../components/shared/timeline/composerDueDate';
import { DealStatusControl } from '../../components/deals/DealStatusControl';
import StatusUpdateDrawer from '../../components/deals/StatusUpdateDrawer';
import { DealReferrerSection } from '../../components/referrers/DealReferrerSection';
import FattureInCloudSection from '../../components/deals/FattureInCloudSection';
import { EnhancedFattureInCloudSection } from '../../components/deals/EnhancedFattureInCloudSection';
import { DealVariablesBillingSection } from '../../components/deals/DealVariablesBillingSection';
import { DealCommissionDisplay } from '../../components/deals/DealCommissionDisplay';
import { DealContactsSection } from '../../components/deals/DealContactsSection';

// Import modals
import TemplateModalWrapper from '../../components/deals/modals/TemplateModalWrapper';
import SendProposalModal from '../../components/deals/modals/SendProposalModal';
import ContractUploadModal from '../../components/deals/modals/ContractUploadModal';
import { AcceptanceDrawer } from '../../components/deals/acceptance-drawer/AcceptanceDrawer';
import UnsignedContractUploadModal from '../../components/deals/modals/UnsignedContractUploadModal';
import SignatureShareModal from '../../components/deals/modals/SignatureShareModal';

// Import custom hooks
import { useLoadDeal } from '../../hooks/deals/useLoadDeal';
import { shouldAskOfferDateRefresh, formatOfferDate } from '../../lib/utils/offerDate';
import { usePDFGeneration } from '../../hooks/deals/usePDFGeneration';
import { useContractUpload } from '../../hooks/deals/useContractUpload';
import { useSignatureSharing } from '../../hooks/deals/useSignatureSharing';
import { useDealActionsHandler } from '../../hooks/deals/useDealActionsHandler';
import { usePageHeader } from '../../hooks/usePageHeader';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { Edit2, FileText, Upload, FileCheck, Copy, Trash2, RefreshCw, Download, Send, Video, MoreHorizontal, ChevronLeft, MessageSquareShare, X } from 'lucide-react';
import { ShareToChatModal } from '../../components/team-chat/ShareToChatModal';
import { MeetingRecordingsList } from '../../components/meetings/MeetingRecordingsList';
import { useBreakpoints } from '../../lib/hooks/useBreakpoints';

interface DealDetailsPageProps {
  dealId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
  onRequestEdit?: (dealId: string) => void;
  onActivityChanged?: () => void;
}

export function DealDetailsPage({ dealId: propDealId, isDrawer = false, onClose, onRequestEdit, onActivityChanged }: DealDetailsPageProps = {}) {
  const fluPilot=useFluPilot();
  const { id: paramId } = useParams<{ id: string }>();
  const id = propDealId || paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // In drawer mode, don't touch URL params (they belong to parent DealsPage)
  const clearEditParam = React.useCallback(() => {
    if (!isDrawer) setSearchParams({}, { replace: true });
  }, [isDrawer, setSearchParams]);
  const { toast, language, confirm } = useApp();
  const { user } = useAuth();
  const [editDrawerClosing, setEditDrawerClosing] = useState(false);


  // Get translations based on current language
  const t = dealsTranslations[language as keyof typeof dealsTranslations] || dealsTranslations.en;
  
  // Store hooks
  const dealStore = useDealStore();
  const dealServiceStore = useDealServiceStore();
  const clientStore = useClientStore();
  const signatureStore = useSignatureStore();
  const settingsStore = useSettingsStore();
  const contractGate = useContractClientGate();
  const [acceptanceWizardOpen, setAcceptanceWizardOpen] = useState(false);
  
  // Destructure what we need from the stores
  const {
    updateDeal,
    updateDealStatus,
    updateDealNote,
    fetchNoteVersions,
    deleteDealNote,
    deleteDealActivity,
    createDeal,
    deleteDeal
  } = dealStore;
  const { dealServices, fetchDealServices, createDealService } = dealServiceStore;
  const { selectedClient } = clientStore;
  const { generateSignatureUrl } = signatureStore;
  // Quali template sono adatti ai servizi di QUESTA offerta. `dealServices` e'
  // gia' caricato qui, quindi l'hook non rifà la query.
  const templatePerOfferta = useTemplatesForDeal(id, dealServices);
  // Lista completa per generazione PDF/firma: DEVE essere la stessa sorgente del
  // picker (l'hook), non lo store — lo store qui non viene mai fetchato e a store
  // vuoto la generazione falliva con "Template not found" (bug Stefano 17/08).
  const allDealTemplates = useMemo(
    () => [...templatePerOfferta.suitable, ...templatePerOfferta.unsuitable],
    [templatePerOfferta.suitable, templatePerOfferta.unsuitable]
  );
  const { settings } = settingsStore;

  // Use the custom hooks for functionality
  const {
    currentDeal, 
    setCurrentDeal,
    loading, 
    error,
    dealNotes,
    loadingNotes,
    loadDealNotes,
    refresh
  } = useLoadDeal(id);

  // Memoize the deal ID to prevent unnecessary re-renders of child components
  const memoizedDealId = useMemo(() => currentDeal?.id, [currentDeal?.id]);

  const {
    quoteNumberCalculated,
    setQuoteNumberCalculated,
    generatingPdf,
    debugMode,
    setDebugMode,
    generateDocument,
    generatePDF,
    calculateQuoteNumber
  } = usePDFGeneration();

  const {
    contractFile,
    setContractFile,
    uploadingContract,
    dragActive,
    setDragActive,
    contractBucketName,
    contractFilePath,
    fileInputRef,
    signingDate,
    setSigningDate,
    showFileTooLargeModal,
    setShowFileTooLargeModal,
    handleContractUpload,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    extractPathFromContractUrl
  } = useContractUpload(t);

  const {
    selectedTemplateId,
    setSelectedTemplateId,
    generatingSignatureLink,
    signatureLink,
    shareMethod,
    setShareMethod,
    signatureLinkCopied,
    handleGenerateSignatureLink,
    handleShareSignatureLink,
    handleCopySignatureLink
  } = useSignatureSharing(t);

  const {
    showEditForm,
    setShowEditForm,
    showTemplateModal,
    setShowTemplateModal,
    showFileUploadModal,
    setShowFileUploadModal,
    showSignatureLinkModal,
    setShowSignatureLinkModal,
    showStatusUpdateDrawer,
    setShowStatusUpdateDrawer,
    showContractViewerModal,
    setShowContractViewerModal,
    showSendProposalModal,
    setShowSendProposalModal,
    handleUpdateDeal
  } = useDealActionsHandler(currentDeal, updateDeal, fetchDealServices, refresh);

  const closeEditDrawer = React.useCallback(() => {
    setEditDrawerClosing(true);
    setTimeout(() => {
      setShowEditForm(false);
      clearEditParam();
      setEditDrawerClosing(false);
    }, 250);
  }, [setShowEditForm, clearEditParam]);

  // State for unsigned contract upload modal
  const [showUnsignedContractModal, setShowUnsignedContractModal] = useState(false);
  const [unsignedContractFile, setUnsignedContractFile] = useState<File | null>(null);
  const [uploadingUnsignedContract, setUploadingUnsignedContract] = useState(false);
  const [unsignedDragActive, setUnsignedDragActive] = useState(false);
  const unsignedFileInputRef = React.useRef<HTMLInputElement>(null);

  // Permissions
  const { canEditDeal, canGenerateDealPDF, canUseAIMeetingBot } = usePermissions();

  // Mobile responsive
  const { isMobile } = useBreakpoints();
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [showShareToChat, setShowShareToChat] = useState(false);

  // Segnalazione 9714fe60: la data stampata nel PDF è deals.created_at
  // ({{deal.date}}). Al clic su "Genera PDF" si chiede se portarla a oggi; la
  // modifica si applica solo quando il documento viene davvero generato.
  const aggiornaDataOffertaRef = useRef(false);
  const apriGeneraPdf = async () => {
    setDebugMode(false);
    aggiornaDataOffertaRef.current = false;
    if (currentDeal && canEditDeal && shouldAskOfferDateRefresh(currentDeal.created_at)) {
      aggiornaDataOffertaRef.current = await confirm({
        title: "Aggiornare la data dell'offerta?",
        message: `L'offerta è datata ${formatOfferDate(currentDeal.created_at)}. Vuoi portarla a oggi (${formatOfferDate(new Date())}), così il cliente riceve il documento con la data corretta?`,
        confirmText: 'Aggiorna data e genera',
        cancelText: 'Mantieni data',
        confirmVariant: 'primary',
      });
    }
    setShowTemplateModal(true);
  };

  const applicaDataOggiSeRichiesta = async () => {
    if (!currentDeal || !aggiornaDataOffertaRef.current) return;
    aggiornaDataOffertaRef.current = false;
    const oggi = new Date().toISOString();
    try {
      await updateDeal(currentDeal.id, { created_at: oggi });
      setCurrentDeal(prev => prev ? { ...prev, created_at: oggi } : prev);
    } catch (error) {
      console.error('Error updating offer date:', error);
      toast.error("Data dell'offerta non aggiornata: il documento mantiene la data precedente");
    }
  };

  // Handle unsigned contract deletion
  const handleDeleteUnsignedContract = async () => {
    if (!currentDeal?.unsigned_contract_url) return;

    const confirmed = await confirm({
      title: 'Elimina Proposta',
      message: 'Sei sicuro di voler eliminare la proposta personalizzata caricata?',
      confirmLabel: 'Elimina',
      cancelLabel: 'Annulla',
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      const url = currentDeal.unsigned_contract_url;
      // Extract file path from URL - look for the path after the bucket name
      const pathMatch = url.match(/\/(contracts|uploads)\/(.+?)(\?|$)/);
      if (pathMatch) {
        const bucket = pathMatch[1];
        const filePath = decodeURIComponent(pathMatch[2]);
        await supabase.storage.from(bucket).remove([filePath]);
      }

      await updateDeal(currentDeal.id, {
        unsigned_contract_url: null as any
      });

      setCurrentDeal(prev => prev ? { ...prev, unsigned_contract_url: null } : null);

      toast.success('Proposta personalizzata eliminata');
    } catch (error) {
      console.error('Error deleting unsigned contract:', error);
      toast.error('Errore nell\'eliminazione della proposta');
    }
  };

  // Apre la proposta non firmata rigenerando un signed URL fresco dal path.
  // Necessario perché l'URL salvato può essere scaduto e/o puntare al vecchio
  // progetto Ohio (in pausa); il path è identico su EU.
  const handleOpenUnsignedProposal = async () => {
    if (!currentDeal?.unsigned_contract_url) return;
    const fresh = await getFreshContractUrl(currentDeal.unsigned_contract_url);
    window.open(fresh, '_blank');
  };

  // DynamicHeader configuration
  const headerConfig = useMemo(() => {
    if (!currentDeal) return null;

    // Build actions array for dropdown menu (less common actions)
    const actions: Array<{
      id: string;
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      variant?: 'default' | 'danger' | 'success';
    }> = [];

    if (currentDeal.signed_contract_url) {
      const contractUrl = currentDeal.signed_contract_url;
      actions.push({
        id: 'view-contract',
        label: t.contract?.view || 'Visualizza Contratto',
        icon: <FileCheck className="h-4 w-4" />,
        onClick: () => {
          handleViewContract(contractUrl);
        }
      });
      actions.push({
        id: 'replace-contract',
        label: 'Ricarica Contratto',
        icon: <RefreshCw className="h-4 w-4" />,
        onClick: () => setShowFileUploadModal(true)
      });
    }

    // Upload/Replace unsigned contract action
    if (canEditDeal) {
      actions.push({
        id: 'upload-unsigned-contract',
        label: currentDeal.unsigned_contract_url ? 'Sostituisci Proposta' : 'Carica Proposta Personalizzata',
        icon: <Upload className="h-4 w-4" />,
        onClick: () => setShowUnsignedContractModal(true)
      });
    }

    // Delete unsigned contract action
    if (canEditDeal && currentDeal.unsigned_contract_url) {
      actions.push({
        id: 'delete-unsigned-contract',
        label: 'Elimina Proposta',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: handleDeleteUnsignedContract,
        variant: 'danger'
      });
    }

    actions.push({
      id: 'duplicate',
      label: 'Duplica Offerta',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => handleDuplicateDeal(),
      variant: 'success'
    });

    actions.push({
      id: 'delete',
      label: 'Elimina Offerta',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: () => {
        handleDeleteDeal();
      },
      variant: 'danger'
    });

    // Custom buttons for header (Genera, Scarica Proposta, Carica/Visualizza PDF Firmato)
    // Notion-like: secondary discreti con border-black/10, primaria "Invia" blu solid
    const secondaryBtn = 'inline-flex items-center gap-1.5 px-2.5 h-8 text-[12px] font-medium rounded-md border border-black/[0.08] dark:border-white/15 text-[#37352F] dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors';
    const primaryBtn = 'inline-flex items-center gap-1.5 px-3 h-8 text-[12px] font-medium rounded-md bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 transition-colors';
    const customRightButtons = (
      <div className="flex items-center gap-1.5">
        {canEditDeal && (
          <button
            onClick={() => { setShowEditForm(true); if (!isDrawer) setSearchParams({ edit: 'true' }, { replace: true }); }}
            className={secondaryBtn}
            title="Modifica offerta"
          >
            <Edit2 className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Modifica</span>
          </button>
        )}
        <button
          onClick={() => setShowShareToChat(true)}
          className={secondaryBtn}
          title="Condividi in chat"
        >
          <MessageSquareShare className="w-3.5 h-3.5" />
        </button>
        {canGenerateDealPDF && (
          <>
            <button
              onClick={() => { void apriGeneraPdf(); }}
              className={secondaryBtn}
              title="Genera PDF"
            >
              <FileText className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Genera PDF</span>
            </button>
            <button
              onClick={() => setShowSendProposalModal(true)}
              className={primaryBtn}
              title="Invia Proposta al cliente"
            >
              <Send className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Invia proposta</span>
            </button>
          </>
        )}
        {currentDeal.unsigned_contract_url && (
          <button
            onClick={handleOpenUnsignedProposal}
            className={secondaryBtn}
            title="Scarica Proposta Personalizzata"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Scarica proposta</span>
          </button>
        )}
        {currentDeal.signed_contract_url ? (
          <button
            onClick={() => handleViewContract(currentDeal.signed_contract_url!)}
            className={secondaryBtn}
            title="Visualizza PDF Firmato"
          >
            <FileCheck className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
            <span className="hidden lg:inline">Vedi firmato</span>
          </button>
        ) : (currentDeal as any).acceptance_draft ? (
          <button
            onClick={() => setAcceptanceWizardOpen(true)}
            className={`${secondaryBtn} !border-amber-400 !text-amber-600 dark:!text-amber-400`}
            title="Completa la firma del contratto"
          >
            <Upload className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            <span className="hidden lg:inline font-semibold">Completa firma</span>
          </button>
        ) : (
          <button
            onClick={() => setAcceptanceWizardOpen(true)}
            className={secondaryBtn}
            title="Carica PDF Firmato"
          >
            <Upload className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Carica firmato</span>
          </button>
        )}
      </div>
    );

    // When editing, hide back button and actions
    if (showEditForm) {
      return {
        sectionId: 'deal-detail',
        showBackButton: false,
        actions: [],
        customRight: undefined
      };
    }

    return {
      sectionId: 'deal-detail',
      showBackButton: !isDrawer,
      onBack: isDrawer ? undefined : () => { navigate(-1); },
      actions,
      customRight: customRightButtons,
      customRightKey: `${currentDeal.unsigned_contract_url || ''}-${currentDeal.signed_contract_url || ''}`
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeal, canEditDeal, canGenerateDealPDF, t, navigate, location.state, setSearchParams, setShowEditForm, showEditForm, setShowTemplateModal, setShowFileUploadModal, setDebugMode]);

  usePageHeader(headerConfig);

  // Set AI entity context for AI Help Chat
  useEffect(() => {
    const { setEntityContext } = useAiHelpStore.getState();
    if (currentDeal) {
      setEntityContext({
        type: 'deal',
        id: currentDeal.id,
        summary: {
          title: currentDeal.title,
          status: currentDeal.status,
          amount: currentDeal.amount,
          client: currentDeal.client?.business_name,
          assignee: currentDeal.assignee?.full_name,
          services: currentDeal.deal_services?.map((s: any) => s.service_name),
          created_at: currentDeal.created_at,
          closing_probability: currentDeal.closing_probability,
        },
      });
    }
    return () => setEntityContext(null);
  }, [currentDeal?.id, currentDeal?.status, currentDeal?.amount]);

  // Check for openActivityDrawer in location state on mount
  useEffect(() => {
    if (location.state && 'openActivityDrawer' in location.state) {
      setStatusDrawerDefaultTab('activity');
      setStatusSlotDefaultOpen(false);
      setShowStatusUpdateDrawer(true);

      // Clear the state after handling it
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Extract contract path info when deal loads (so it's ready when user clicks "Visualizza Firmato")
  useEffect(() => {
    if (currentDeal?.signed_contract_url) {
      extractPathFromContractUrl(currentDeal.signed_contract_url);
    }
  }, [currentDeal?.signed_contract_url, extractPathFromContractUrl]);

  // Update currentDeal.client when selectedClient changes
  useEffect(() => {
    if (
      currentDeal &&
      selectedClient &&
      currentDeal.client_id === selectedClient.id &&
      (!currentDeal.client || currentDeal.client.id !== selectedClient.id)
    ) {
      setCurrentDeal(prev => prev ? { ...prev, client: selectedClient } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id, currentDeal?.client_id, currentDeal?.client?.id]);

  // State for editing activities
  const [editingActivity, setEditingActivity] = useState<any>(null);

  // Tab di default per StatusUpdateDrawer (apre su 'note' o 'activity')
  const [statusDrawerDefaultTab, setStatusDrawerDefaultTab] = useState<'note' | 'activity'>('note');
  // Pre-fill del drawer valorizzato SOLO dall'escalation del composer inline
  // ("Apri nel drawer completo"), azzerato in `onClose`.
  const [composerNoteText, setComposerNoteText] = useState<string | null>(null);
  const [composerActivityData, setComposerActivityData] = useState<
    { title: string; type: string; description: string; due_date?: string; due_time?: string } | null
  >(null);

  // Apre lo slot "Stato offerta" già espanso (redesign T7, eccezione 2): true SOLO quando il
  // drawer si apre dal badge di stato (`onStatusUpdate` su DealClientInfo, il vero controllo
  // di cambio stato); tutte le altre aperture (nota/attività/edit) lo tengono chiuso.
  const [statusSlotDefaultOpen, setStatusSlotDefaultOpen] = useState(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState<'quote' | 'costs' | 'commissions' | 'recordings'>('quote');

  // Handler for editing activities — apre il nuovo StatusUpdateDrawer in modalità edit
  const handleEditActivity = (activity: any) => {
    setEditingActivity(activity);
    setStatusDrawerDefaultTab('activity');
    setStatusSlotDefaultOpen(false);
    setShowStatusUpdateDrawer(true);
  };

  const {organizationId: documentOrganizationId}=useUserData();
  const documentContext=React.useRef({active:true,key:''});
  documentContext.current.key=[documentOrganizationId,user?.id,currentDeal?.id].join(':');
  useEffect(()=>{documentContext.current.active=true;return()=>{documentContext.current.active=false;};},[]);
  const documentActionPending=React.useRef(false);
  const runDocumentAction=async(action:(deal:NonNullable<typeof currentDeal>)=>Promise<void>)=>{
    if(!currentDeal)return;
    if(!fluPilot){await action(currentDeal);return;}
    if(documentActionPending.current)return;
    documentActionPending.current=true;
    const key=documentContext.current.key;
    const isCurrent=()=>documentContext.current.active&&documentContext.current.key===key;
    let actionStarted=false;
    try{
      if(!settings||!selectedTemplateId||!allDealTemplates.some(t=>t.id===selectedTemplateId))throw Error('document_template_required');
      const number=await prepareOfferDocumentNumber({
        organizationId:documentOrganizationId||'',dealId:currentDeal.id,
        canGenerate:canGenerateDealPDF,canUpdate:canEditDeal,isCurrent,
        read:async()=>{
          const {data,error}=await supabase.from('deals').select('id,organization_id,quote_number')
            .eq('organization_id',documentOrganizationId).eq('id',currentDeal.id).single();
          if(error)throw error;return data;
        },
        calculate:async()=>{
          await calculateQuoteNumber({...currentDeal,quote_number:null},settings,updateDeal);
        }
      });
      if(!isCurrent())return;
      actionStarted=true;
      await action({...currentDeal,quote_number:number});
    }catch(error){
      if(isCurrent())toast.error(actionStarted?'La generazione non è stata completata. Verifica lo stato del documento prima di riprovare.':error instanceof Error&&error.message==='document_template_required'
        ?'Scegli un modello prima di generare il documento.'
        :'Non posso verificare il numero dell’offerta. Nessun documento o link è stato generato.');
    }finally{documentActionPending.current=false;}
  };

  // Generate quote number when deal and settings are loaded
  // Nota: updateDeal è escluso dalle dipendenze per evitare loop infiniti (è stabile nel store)
  useEffect(() => {
    if (documentOrganizationId && !fluPilot && currentDeal && settings && !quoteNumberCalculated) {
      calculateQuoteNumber(currentDeal, settings, updateDeal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentOrganizationId, fluPilot, currentDeal?.id, settings, quoteNumberCalculated, calculateQuoteNumber]);
  
  // Caricamento asincrono delle note e delle attività - usa un ref per tracciare se è già stato caricato
  const initialLoadDoneRef = React.useRef(false);
  
  useEffect(() => {
    // Carica solo una volta quando il deal è pronto e non è già stato caricato
    if (id && loadDealNotes && currentDeal && !initialLoadDoneRef.current) {
      // Impostiamo un timeout minimo per assicurarci che il componente principale sia già renderizzato
      const timer = setTimeout(() => {
                
        loadDealNotes().then(() => {
                    // Segnala che il caricamento iniziale è stato completato
          initialLoadDoneRef.current = true;
        });
      }, 300);  // Delay minimizzato poiché ora gestiamo tutto direttamente
      
      return () => clearTimeout(timer);
    }
    // We're removing loadDealNotes from dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentDeal?.__initial_load]);

  // Realtime subscription for deal_notes changes
  useEffect(() => {
    if (!id) return;

    const subscription = supabase
      .channel(`deal-notes-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events: INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'deal_notes',
          filter: `deal_id=eq.${id}`
        },
        (payload) => {
          console.log('Deal notes updated via Realtime:', payload);
          if (loadDealNotes) {
            loadDealNotes();
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id, loadDealNotes]);

  // Realtime subscription for sales_activities changes
  useEffect(() => {
    if (!id) return;

    const subscription = supabase
      .channel(`deal-activities-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events: INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'sales_activities',
          filter: `deal_id=eq.${id}`
        },
        (payload) => {
          console.log('Deal activities updated via Realtime:', payload);
          if (loadDealNotes) {
            // loadDealNotes() also refreshes activities
            loadDealNotes();
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id, loadDealNotes]);

  // Check for edit parameter in URL
  useEffect(() => {
    // In drawer mode, ignore URL params (they belong to parent DealsPage)
    if (isDrawer) return;
    if (searchParams.get('edit') === 'true') {
      setShowEditForm(true);
      if (currentDeal?.id) {
        fetchDealServices(currentDeal.id);
      }
    }
  }, [searchParams, setShowEditForm, currentDeal?.id, fetchDealServices, isDrawer]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handlers for template and document generation
  const handleGenerateDocument = async (format: 'pdf' | 'docx' = 'pdf', serviceIds?: string[]) => {
    if (!currentDeal) return;
    await runDocumentAction(async documentDeal=>{
    await applicaDataOggiSeRichiesta();
    await generateDocument(documentDeal, selectedTemplateId, dealServices, settings, allDealTemplates, format, serviceIds);
    setShowTemplateModal(false);
    });
  };

  // Legacy handler for backward compatibility
  const handleGeneratePDF = async () => {
    await handleGenerateDocument('pdf');
  };

  // Handler for generating signature link
  // GATE dati cliente: modale di verifica/conferma sempre visibile prima di generare
  const handleGenerateSignatureLinkClick = async () => {
    if (!currentDeal) return;
    await contractGate.guard(currentDeal.client_id, () => { void doGenerateSignatureLink(); }, {
      dealId: currentDeal.id,
      clientName: currentDeal.client?.business_name || currentDeal.client?.full_name,
    });
  };

  const doGenerateSignatureLink = async () => {
    await runDocumentAction(async documentDeal=>{
    await applicaDataOggiSeRichiesta();
    await handleGenerateSignatureLink(
      documentDeal,
      generateSignatureUrl,
      updateDeal,
      allDealTemplates,
      () => {
        setShowTemplateModal(false);
        setShowSignatureLinkModal(true);
      }
    );
    });
  };

  // Handle contract upload (contratto FIRMATO)
  // GATE dati cliente: modale di verifica/conferma sempre visibile prima dell'upload
  const handleContractUploadClick = async () => {
    if (!currentDeal) return;
    await contractGate.guard(currentDeal.client_id, () => { void doSignedContractUpload(); }, {
      dealId: currentDeal.id,
      clientName: currentDeal.client?.business_name || currentDeal.client?.full_name,
    });
  };

  const doSignedContractUpload = async () => {
    const result = await handleContractUpload(currentDeal!, updateDeal, updateDealStatus);
    if (result) {
      // Aggiorna lo stato locale subito per riflettere il contratto caricato
      setCurrentDeal(prev => prev ? { ...prev, signed_contract_url: result, status: 'accepted' } : prev);
      setShowFileUploadModal(false);
      await refresh();
    }
  };

  // Handle view contract
  const handleViewContract = (url: string, bucketName?: string, filePath?: string) => {
    // If we have bucket and path information, use it
    if (bucketName && filePath) {
      extractPathFromContractUrl(url);
    }
    // Try to extract from the contract URL if we don't already have path info
    else if (currentDeal?.signed_contract_url) {
      extractPathFromContractUrl(currentDeal.signed_contract_url);
    }

    setShowContractViewerModal(true);
  };

  // Handle unsigned contract file handlers
  const handleUnsignedFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo file PDF sono accettati');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error('Il file è troppo grande (max 10MB)');
        return;
      }
      setUnsignedContractFile(file);
    }
  };

  const handleUnsignedDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setUnsignedDragActive(true);
  };

  const handleUnsignedDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setUnsignedDragActive(false);
  };

  const handleUnsignedDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setUnsignedDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo file PDF sono accettati');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Il file è troppo grande (max 10MB)');
        return;
      }
      setUnsignedContractFile(file);
    }
  };

  // Handle unsigned contract upload
  // GATE dati cliente: se mancano campi obbligatori apre il modale di completamento,
  // poi (al salvataggio) procede con l'upload. Altrimenti carica subito.
  const handleUnsignedContractUpload = async () => {
    if (!unsignedContractFile || !currentDeal) return;
    await contractGate.guard(currentDeal.client_id, () => { void doUnsignedContractUpload(); }, {
      dealId: currentDeal.id,
      clientName: currentDeal.client?.business_name || currentDeal.client?.full_name,
    });
  };

  const doUnsignedContractUpload = async () => {
    if (!unsignedContractFile || !currentDeal) return;
    try {
      setUploadingUnsignedContract(true);

      // Use organization_id from deal or get from user profile
      const organizationId = currentDeal.organization_id || 'shared';

      // Upload to storage - use same path pattern as signed contracts
      // Sanitize filename: replace spaces, remove accents/diacritics, keep only safe chars
      const safeName = unsignedContractFile.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9._\-]/g, '_'); // keep only safe characters
      const fileName = `${organizationId}/proposals/${currentDeal.id}/${safeName}`;
      let contractUrl = '';
      let bucketName = 'contracts';

      // Try contracts bucket first
      const { error: uploadError } = await supabase.storage
        .from('contracts')
        .upload(fileName, unsignedContractFile, {
          upsert: true,
          contentType: 'application/pdf'
        });

      if (uploadError) {
        // If the contracts bucket doesn't exist, try with uploads bucket
        if (uploadError.message.includes('Bucket not found')) {
          bucketName = 'uploads';
          const { error: uploadError2 } = await supabase.storage
            .from('uploads')
            .upload(fileName, unsignedContractFile, {
              upsert: true,
              contentType: 'application/pdf'
            });

          if (uploadError2) throw uploadError2;

          // Get the public URL from uploads bucket
          const { data: publicUrlData } = supabase.storage
            .from('uploads')
            .getPublicUrl(fileName);

          contractUrl = publicUrlData.publicUrl;
        } else {
          throw uploadError;
        }
      } else {
        // For contracts bucket, get a signed URL (same as signed contracts)
        try {
          const { data: signedUrlData } = await supabase.storage
            .from('contracts')
            .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year expiry

          if (signedUrlData?.signedUrl) {
            contractUrl = signedUrlData.signedUrl;
          } else {
            // Fall back to public URL
            const { data: publicUrlData } = supabase.storage
              .from('contracts')
              .getPublicUrl(fileName);
            contractUrl = publicUrlData.publicUrl;
          }
        } catch {
          // Fall back to public URL
          const { data: publicUrlData } = supabase.storage
            .from('contracts')
            .getPublicUrl(fileName);
          contractUrl = publicUrlData.publicUrl;
        }
      }

      if (!contractUrl) throw new Error('Failed to get file URL');

      // Update deal with unsigned contract URL
      await updateDeal(currentDeal.id, {
        unsigned_contract_url: contractUrl
      });

      setCurrentDeal(prev => prev ? { ...prev, unsigned_contract_url: contractUrl } : null);

      toast.success('Proposta personalizzata caricata con successo');
      setShowUnsignedContractModal(false);
      setUnsignedContractFile(null);
      if (unsignedFileInputRef.current) {
        unsignedFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading unsigned contract:', error);
      toast.error('Errore nel caricamento del contratto');
    } finally {
      setUploadingUnsignedContract(false);
    }
  };

  // Handle deal duplication
  const handleDuplicateDeal = async () => {
    if (!currentDeal) return;

    try {
      // Get user data for organization_id
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Calculate total amount from services
      const totalAmount = dealServices?.reduce((sum, service) => {
        const amount = service.discounted_amount || service.amount;
        return sum + amount;
      }, 0) || 0;

      // Check if first service is custom
      const firstService = dealServices?.[0];
      const isFirstServiceCustom = firstService?.service_id?.toString().startsWith('custom-');

      // Create the duplicate deal directly
      const { data: newDeal, error: dealError } = await supabase
        .from('deals')
        .insert({
          title: `${currentDeal.title} (Copia)`,
          description: currentDeal.description,
          client_id: currentDeal.client_id,
          assigned_to: currentDeal.assigned_to,
          amount: totalAmount,
          status: 'draft',
          organization_id: userProfile.organization_id,
          service_id: isFirstServiceCustom ? null : firstService?.service_id,
          payment_method_id: firstService?.payment_method_id,
          quote_number: null,
          signed_contract_url: null,
          closed_at: null,
          signed_at: null,
          is_active: false
        })
        .select()
        .single();

      if (dealError) throw dealError;
      if (!newDeal) throw new Error('Errore nella creazione della copia');

      // Duplicate services if they exist
      if (dealServices && dealServices.length > 0) {
        const servicesData = dealServices.map(service => ({
          deal_id: newDeal.id,
          service_id: service.service_id,
          amount: service.amount,
          discounted_amount: service.discounted_amount,
          description: service.description,
          payment_schedule: service.payment_schedule,
          monthly_duration: service.monthly_duration,
          custom_service_name: service.custom_service_name,
          order: service.order || 0,
          organization_id: userProfile.organization_id
        }));

        const { error: servicesError } = await supabase
          .from('deal_services')
          .insert(servicesData);

        if (servicesError) {
          console.error('Error creating services:', servicesError);
          // Don't throw here, the deal was created successfully
          toast.error('Deal duplicato ma errore nella copia dei servizi');
        }
      }

      toast.success('Offerta duplicata con successo');

      // Navigate to the new deal
      navigate(`/deals/${newDeal.id}`);
    } catch (error) {
      console.error('Error duplicating deal:', error);
      toast.error('Errore nella duplicazione dell\'offerta');
    }
  };

  // Handle deal deletion (conferma inclusa; ordini di magazzino collegati: vedi dealDeleteOrders)
  const handleDeleteDeal = async () => {
    if (!currentDeal?.id) return;

    try {
      const ordersChoice = await askDealLinkedOrders(currentDeal.id, confirm);
      const confirmed = await confirm({
        title: 'Elimina Offerta',
        message: `Sei sicuro di voler eliminare l'offerta "${currentDeal.title}"?${ordersChoice.summary ? ` ${ordersChoice.summary}` : ''} Questa azione non può essere annullata.`,
        confirmLabel: 'Elimina',
        cancelLabel: 'Annulla',
        type: 'danger'
      });
      if (!confirmed) return;

      const notDeleted = await deleteDeal(currentDeal.id, false, { deleteDraftOrders: ordersChoice.deleteDraftOrders });
      if (notDeleted.length > 0) {
        toast.error(`Offerta eliminata, ma non è stato possibile eliminare: ${notDeleted.join(', ')}`);
      } else {
        toast.success('Offerta eliminata con successo');
      }
      navigate('/deals');
    } catch (error) {
      console.error('Error deleting deal:', error);
      const detail = useDealStore.getState().error;
      toast.error(`Errore nell'eliminazione dell'offerta${detail ? `: ${detail}` : ''}`);
    }
  };

  // Prepare keyboard shortcut actions
  const shortcutActions = {
    editDeal: () => {
      setShowEditForm(true);
      if (!isDrawer) setSearchParams({ edit: 'true' }, { replace: true });
    },
    generateQuote: () => {
      void apriGeneraPdf();
    },
    uploadContract: () => setShowFileUploadModal(true),
    openStatusUpdate: () => {
      setStatusDrawerDefaultTab('note');
      setStatusSlotDefaultOpen(false);
      setShowStatusUpdateDrawer(true);
    },
    openActivity: () => {
      setStatusDrawerDefaultTab('activity');
      setStatusSlotDefaultOpen(false);
      setShowStatusUpdateDrawer(true);
    },
    refresh: refresh
  };

  // State is ready for rendering decisions

  
  // Dichiariamo gli stati all'inizio
  const [DealFormComponent, setDealFormComponent] = React.useState<any>(null);
  const [formLoadError, setFormLoadError] = React.useState<string | null>(null);
  const [formLoading, setFormLoading] = React.useState<boolean>(false);

  // Caricamento del form
  React.useEffect(() => {
    if (showEditForm && !DealFormComponent && !formLoading) {
      setFormLoading(true);
      import('../../components/deals/DealForm')
        .then(({ DealForm }) => {
                setDealFormComponent(() => DealForm);
        })
        .catch(error => {
          console.error("[DealDetailsPage] FORM_ERROR: Failed to load form:", error);
          setFormLoadError("Failed to load the form. Please try again.");
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  }, [showEditForm, DealFormComponent, formLoading]);

  // Loading state — MinimalLoader Flusia per coerenza brand
  if (loading && !currentDeal) {
    return (
      <div className="flex justify-center items-center h-64">
        <MinimalLoader size="xl" variant="brand" />
      </div>
    );
  }

  // Sessione scaduta — mostrata sia quando error='SESSION_EXPIRED' sia quando deal non trovato + nessuna sessione attiva
  if (error === 'SESSION_EXPIRED' || (!currentDeal && !loading && !error)) {
    const isSessionIssue = error === 'SESSION_EXPIRED';
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-4">
        <div className="text-xl font-medium text-[#37352F]">
          {isSessionIssue ? 'Sessione scaduta' : 'Offerta non trovata'}
        </div>
        <p className="text-sm text-[#787774] max-w-sm">
          {isSessionIssue
            ? 'La tua sessione è scaduta o non è valida. Effettua nuovamente l\'accesso per continuare.'
            : 'L\'offerta richiesta non esiste o non hai i permessi per visualizzarla.'}
        </p>
        {isSessionIssue ? (
          <button
            className="px-4 py-2 bg-[#37352F] text-white text-sm rounded-md hover:bg-[#2f2e2a]"
            onClick={() => {
              supabase.auth.signOut().finally(() => navigate('/login'));
            }}
          >
            Vai al login
          </button>
        ) : (
          <button
            className="px-4 py-2 bg-[#37352F] text-white text-sm rounded-md hover:bg-[#2f2e2a]"
            onClick={() => navigate('/deals')}
          >
            Torna alle offerte
          </button>
        )}
      </div>
    );
  }

  // Generic error state
  if (error) {
    return <div className="text-red-600 p-4">Errore: {error}</div>;
  }
  


  
  try {
    // Standard UI
    return (
    <>
        {/* Template Modal - rendered outside of PageContainer to avoid z-index issues */}
        <TemplateModalWrapper
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          templates={templatePerOfferta.suitable}
          unsuitableTemplates={templatePerOfferta.unsuitable}
          allowUnsuitable={templatePerOfferta.allowUnsuitable}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
          onGenerateDocument={handleGenerateDocument}
          services={dealServices}
          onGeneratePDF={handleGeneratePDF}
          onGenerateSignatureLink={handleGenerateSignatureLinkClick}
          generatingPdf={generatingPdf}
          generatingSignatureLink={generatingSignatureLink}
          debugMode={debugMode}
          translations={t.templateModal}
        />

        {/* Send Proposal Modal */}
        {currentDeal && (
          <SendProposalModal
            isOpen={showSendProposalModal}
            onClose={() => setShowSendProposalModal(false)}
            deal={currentDeal}
            services={dealServices}
            settings={settings}
            templates={templatePerOfferta.suitable}
            unsuitableTemplates={templatePerOfferta.unsuitable}
            allowUnsuitable={templatePerOfferta.allowUnsuitable}
          />
        )}

        <PageContainer className="sidebar-aware-content">
          {/* Enhanced UI toggle button rimosso da qui, spostato nel DealHeader */}

        {/* Render in modo sicuro */}
        {currentDeal ? (
          <>
            {/* Mobile-only action bar - visibile solo su mobile dove il DynamicHeader è nascosto */}
            {isMobile && (
              <div className={fluPilot?"md:hidden mb-3":"sm:hidden mb-3"}>
                {/* Header mobile con titolo e back */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 -ml-2 rounded-lg hover:bg-accent transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-base font-semibold truncate flex-1">{currentDeal.title}</h1>
                  <FluRecordTrigger/>
                </div>
                {/* Azioni principali scrollabili */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 mobile-scroll-x">
                  {canEditDeal && (
                    <button
                      onClick={() => { setShowEditForm(true); if (!isDrawer) setSearchParams({ edit: 'true' }, { replace: true }); }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-white/20 text-foreground hover:bg-accent transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Modifica
                    </button>
                  )}
                  {canGenerateDealPDF && (
                    <>
                      <button
                        onClick={() => { void apriGeneraPdf(); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-white/20 text-foreground hover:bg-accent transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Genera PDF
                      </button>
                      <button
                        onClick={() => setShowSendProposalModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-blue-500/50 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Invia
                      </button>
                    </>
                  )}
                  {currentDeal.unsigned_contract_url && (
                    <button
                      onClick={handleOpenUnsignedProposal}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-blue-500/50 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Proposta
                    </button>
                  )}
                  {currentDeal.signed_contract_url ? (
                    <button
                      onClick={() => handleViewContract(currentDeal.signed_contract_url!)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-white/20 text-foreground hover:bg-accent transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <FileCheck className="w-3.5 h-3.5" />
                      Firmato
                    </button>
                  ) : (currentDeal as any).acceptance_draft ? (
                    <button
                      onClick={() => setAcceptanceWizardOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-amber-400 text-amber-600 dark:text-amber-400 hover:bg-accent transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Completa firma
                    </button>
                  ) : (
                    <button
                      onClick={() => setAcceptanceWizardOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-white/20 text-foreground hover:bg-accent transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Carica Firmato
                    </button>
                  )}
                  {/* Menu azioni secondarie */}
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => setShowMobileActions(!showMobileActions)}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/20 text-foreground hover:bg-accent transition-colors"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                    {showMobileActions && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowMobileActions(false)} />
                        <div className="absolute right-0 top-full mt-1 z-50 w-56 glass-card border border-white/10 rounded-lg shadow-xl py-1">
                          <button
                            onClick={() => { handleDuplicateDeal(); setShowMobileActions(false); }}
                            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                            Duplica Offerta
                          </button>
                          {canEditDeal && (
                            <button
                              onClick={() => { setShowUnsignedContractModal(true); setShowMobileActions(false); }}
                              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                            >
                              <Upload className="w-4 h-4" />
                              {currentDeal.unsigned_contract_url ? 'Sostituisci Proposta' : 'Carica Proposta'}
                            </button>
                          )}
                          {canEditDeal && currentDeal.unsigned_contract_url && (
                            <button
                              onClick={() => { setShowMobileActions(false); handleDeleteUnsignedContract(); }}
                              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Elimina Proposta
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              setShowMobileActions(false);
                              handleDeleteDeal();
                            }}
                            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Elimina Offerta
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Main content container with responsive columns */}
            <div className="flex flex-col md:flex-row">
              {/* Left column - Deal details */}
              <div className="w-full md:w-3/5 md:pr-4 mb-4 md:mb-0">
                <div className="bg-white dark:bg-gray-800/50 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-xl h-full">
                  <DealClientInfo
                    deal={currentDeal}
                    isDrawer={isDrawer}
                    clientName={currentDeal.client?.business_name || selectedClient?.business_name || 'Unknown Client'}
                    translations={t}
                    onClientClick={() => {
                      if (currentDeal.client_id) {
                        window.dispatchEvent(new CustomEvent('open-client-modal', {
                          detail: { entityId: currentDeal.client_id }
                        }));
                      }
                    }}
                    onStatusUpdate={() => {
                      // Vero controllo di cambio stato (badge stato): apre lo slot già
                      // espanso (redesign T7, eccezione 2). Tab invariato (era già così
                      // prima del redesign: nessun `setStatusDrawerDefaultTab` qui).
                      setStatusSlotDefaultOpen(true);
                      setShowStatusUpdateDrawer(true);
                    }}
                    onStatusChange={async (status: string, note?: string) => {
                      await updateDealStatus(currentDeal.id, status, note);

                      // Immediate refresh for most data
                      await refresh();

                      // If deal was accepted, reload activities after tasks are created
                      if (status === 'accepted') {
                        setTimeout(async () => {
                          console.log('[DealDetailsPage] Reloading activities after deal acceptance');

                          // Fetch activities directly from database with user profile joins
                          const { data: activities, error } = await supabase
                            .from('sales_activities')
                            .select(`
                              id,
                              title,
                              description,
                              type,
                              status,
                              due_date,
                              user_id,
                              assigned_to,
                              created_at,
                              completed_at,
                              outcome,
                              users:user_profiles!sales_activities_user_id_fkey1(
                                id,
                                email,
                                full_name,
                                profile_image_url,
                                avatar_url
                              ),
                              assignee:user_profiles!sales_activities_user_id_fkey(
                                id,
                                email,
                                full_name,
                                profile_image_url,
                                avatar_url
                              )
                            `)
                            .eq('deal_id', currentDeal.id)
                            .order('created_at', { ascending: false });

                          if (!error && activities) {
                            console.log(`✅ Loaded ${activities.length} activities for deal`);
                            setCurrentDeal(prev => prev ? { ...prev, activities } : null);
                          } else if (error) {
                            console.error('Error loading activities:', error);
                          }
                        }, 3500); // 3.5 seconds to ensure backend has finished creating tasks
                      }
                    }}
                    onEditSignedDate={(date) => {
                      updateDeal(currentDeal.id, {
                        signed_at: date
                      }).then(() => {
                        toast.success(t.signature?.dateUpdated || 'Signed date updated');
                        // Update local state
                        setCurrentDeal(prev => prev ? { ...prev, signed_at: date } : null);
                      });
                    }}
                  />
                  
                  {/* Contract Upload Modal */}
                  <ContractUploadModal
                    isOpen={showFileUploadModal}
                    onClose={() => setShowFileUploadModal(false)}
                    onUpload={handleContractUploadClick}
                    contractFile={contractFile}
                    uploadingContract={uploadingContract}
                    dragActive={dragActive}
                    fileInputRef={fileInputRef}
                    onFileChange={handleFileChange}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onRemoveFile={() => {
                      setContractFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    signingDate={signingDate}
                    onSigningDateChange={setSigningDate}
                    translations={t}
                  />

                  {/* Drawer accettazione (carica firmato / completa firma con bozza) */}
                  {acceptanceWizardOpen && (
                    <AcceptanceDrawer
                      isOpen={acceptanceWizardOpen}
                      onClose={() => setAcceptanceWizardOpen(false)}
                      deal={currentDeal}
                      client={currentDeal.client ?? null}
                      requiredFields={((settingsStore.settings as any)?.required_client_fields_for_contract as string[]) ?? []}
                      clientName={currentDeal.client?.business_name}
                      // refresh() aggiorna l'offerta MOSTRATA (useLoadDeal): fetchDealById
                      // aggiornava solo la copia nello store e il firmato compariva dopo il ricarico.
                      onCompleted={() => { void refresh(); }}
                    />
                  )}

                  {/* Unsigned Contract Upload Modal */}
                  <UnsignedContractUploadModal
                    isOpen={showUnsignedContractModal}
                    onClose={() => {
                      setShowUnsignedContractModal(false);
                      setUnsignedContractFile(null);
                      if (unsignedFileInputRef.current) {
                        unsignedFileInputRef.current.value = '';
                      }
                    }}
                    onUpload={handleUnsignedContractUpload}
                    contractFile={unsignedContractFile}
                    uploading={uploadingUnsignedContract}
                    dragActive={unsignedDragActive}
                    fileInputRef={unsignedFileInputRef}
                    onFileChange={handleUnsignedFileChange}
                    onDragOver={handleUnsignedDragOver}
                    onDragLeave={handleUnsignedDragLeave}
                    onDrop={handleUnsignedDrop}
                    onRemoveFile={() => {
                      setUnsignedContractFile(null);
                      if (unsignedFileInputRef.current) {
                        unsignedFileInputRef.current.value = '';
                      }
                    }}
                  />

                  {/* Gate dati cliente obbligatori (contratto/firma) */}
                  {contractGate.modal}

                  {/* Signature Link Share Modal */}
                  <SignatureShareModal
                    isOpen={showSignatureLinkModal}
                    onClose={() => setShowSignatureLinkModal(false)}
                    signatureLink={signatureLink}
                    shareMethod={shareMethod}
                    onShareMethodChange={setShareMethod}
                    onShare={() => handleShareSignatureLink(() => setShowSignatureLinkModal(false))}
                    translations={t}
                    signatureLinkCopied={signatureLinkCopied}
                  />

                  {/* Edit Deal — in-content drawer (portaled to layout root to escape stacking context) */}
                  {(showEditForm || editDrawerClosing) && !!currentDeal && createPortal(
                    <>
                      <div
                        className={`fixed inset-0 z-[65] bg-black/10 md:top-12 max-md:!left-0 ${editDrawerClosing ? 'animate-[fadeOut_0.25s_ease-in_forwards]' : 'animate-[fadeIn_0.3s_ease-out]'}`}
                        style={{ left: 'var(--sidebar-width, 0px)' }}
                        onClick={(e) => { e.stopPropagation(); closeEditDrawer(); }}
                      />
                      <div
                        className={`fixed top-0 right-0 bottom-0 z-[66] bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto md:top-12 max-md:!left-0 ${editDrawerClosing ? 'animate-[slideOutRight_0.25s_ease-in_forwards]' : 'animate-[slideInRight_0.25s_ease-out]'}`}
                        style={{ left: 'var(--sidebar-width, 0px)' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); closeEditDrawer(); }}
                          className="absolute top-3 left-2 z-50 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-black/5 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-white/5 transition-colors"
                          title="Chiudi"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {DealFormComponent && (
                          <DealFormComponent
                            initialData={currentDeal}
                            onSubmit={async (data: any, services: any[], commissions?: any, referrerIds?: string[], contactIds?: string[]) => {
                              await handleUpdateDeal(data, services, commissions, referrerIds, contactIds);
                              setShowEditForm(false);
                              clearEditParam();
                            }}
                            onCancel={closeEditDrawer}
                          />
                        )}
                        {!DealFormComponent && formLoading && (
                          <div className="flex justify-center items-center h-64">
                            <MinimalLoader size="xl" variant="brand" />
                          </div>
                        )}
                        {formLoadError && (
                          <div className="text-red-600 p-4">{formLoadError}</div>
                        )}
                      </div>
                    </>,
                    document.getElementById('layout-root') || document.body
                  )}

                  {/* Tab Navigation - Notion pill style */}
                  <div className="mb-4 md:mb-5 px-4 md:px-6">
                    <nav className="inline-flex bg-[rgba(55,53,47,0.06)] dark:bg-white/10 rounded-md p-0.5" aria-label="Tabs">
                      <button
                        onClick={() => setActiveTab('quote')}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 whitespace-nowrap ${
                          activeTab === 'quote'
                            ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        Offerta
                      </button>
                      <button
                        onClick={() => setActiveTab('costs')}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 whitespace-nowrap ${
                          activeTab === 'costs'
                            ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        Costi
                      </button>
                      <button
                        onClick={() => setActiveTab('commissions')}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 whitespace-nowrap ${
                          activeTab === 'commissions'
                            ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        Provvigioni
                      </button>
                      <button
                        onClick={() => setActiveTab('recordings')}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 whitespace-nowrap ${
                          activeTab === 'recordings'
                            ? 'bg-white dark:bg-gray-700 text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        Registrazioni
                      </button>
                    </nav>
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'quote' && (
                    <div className="space-y-6">
                      <DealServicesSection
                        deal={currentDeal}
                        dealServices={dealServices}
                        translations={t}
                      />

                      <DealVariablesBillingSection
                        deal={currentDeal}
                        dealServices={dealServices}
                        translations={t}
                        onDealUpdate={refresh}
                      />

                      {/* Si mostra da solo se l'offerta ha righe prodotto */}
                      <DealOrderSection
                        deal={currentDeal}
                        dealServices={dealServices}
                      />
                    </div>
                  )}

                  {activeTab === 'costs' && (
                    <div className="py-4">
                      <DealCostsSection
                        deal={currentDeal}
                        dealServices={dealServices}
                      />
                    </div>
                  )}

                  {activeTab === 'commissions' && memoizedDealId && (
                    <div className="py-4">
                      <DealCommissionDisplay dealId={memoizedDealId} />
                    </div>
                  )}

                  {activeTab === 'recordings' && memoizedDealId && (
                    <div className="py-4">
                      <MeetingRecordingsList dealId={memoizedDealId} />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Right column - Notes History */}
              <div className="w-full md:w-2/5 md:pl-4">
                {/* Use stable key to prevent unmounting during note loading */}
                <DealNotesSection
                  key={`notes-section-${currentDeal?.id || 'new'}`}
                  deal={currentDeal}
                  dealNotes={dealNotes}
                  dealActivities={currentDeal.activities || []} /* Pass activities explicitly */
                  loadingNotes={loadingNotes}
                  onAttachmentsChanged={() => loadDealNotes()}
                  suppressTour={showEditForm || editDrawerClosing || showStatusUpdateDrawer}
                  composer={{
                    onSaved: async () => {
                      await loadDealNotes();
                      refresh();
                    },
                    onOpenDrawer: (mode, payload) => {
                      setStatusSlotDefaultOpen(false);
                      setEditingActivity(null);
                      if (mode === 'note') {
                        setStatusDrawerDefaultTab('note');
                        setComposerActivityData(null);
                        setComposerNoteText(payload.noteText || null);
                      } else {
                        setStatusDrawerDefaultTab('activity');
                        setComposerNoteText(null);
                        setComposerActivityData({
                          title: payload.title || '',
                          type: payload.type || 'task',
                          description: payload.description || '',
                          due_date: payload.dueDate ? toComposerDateInputValue(payload.dueDate) : undefined,
                          due_time: payload.dueDate ? toComposerTimeInputValue(payload.dueDate) : undefined,
                        });
                      }
                      setShowStatusUpdateDrawer(true);
                    },
                  }}
                  translations={t}
                  formatDate={formatDate}
                  toast={toast}
                  onAddNote={() => {
                    setStatusDrawerDefaultTab('note');
                    setStatusSlotDefaultOpen(false);
                    setShowStatusUpdateDrawer(true);
                  }}
                  onAddTask={() => {
                    setStatusDrawerDefaultTab('activity');
                    setStatusSlotDefaultOpen(false);
                    setShowStatusUpdateDrawer(true);
                  }}
                  onEditNote={async (noteId, content) => {
                    try {
                      // Call the updateDealNote method from dealStore
                      const updatedNote = await updateDealNote(noteId, content);
                      
                      // Refresh notes after update
                      loadDealNotes();
                      
                      toast.success(t.statusUpdate?.noteUpdated || 'Note updated successfully');
                      return updatedNote;
                    } catch (error) {
                      console.error('Error updating note:', error);
                      toast.error(t.statusUpdate?.noteUpdateError || 'Failed to update note');
                      throw error;
                    }
                  }}
                  onFetchNoteVersions={async (noteId) => {
                    try {
                      // Call the fetchNoteVersions method from dealStore
                      const versions = await fetchNoteVersions(noteId);
                      return versions;
                    } catch (error) {
                      console.error('Error fetching note versions:', error);
                      toast.error(t.statusUpdate?.historyFetchError || 'Failed to fetch note history');
                      return [];
                    }
                  }}
                  onDeleteNote={async (noteId) => {
                    try {
                      if (!id) return;
                      
                      // Confirm before deleting
                      if (!window.confirm(t.statusUpdate?.confirmDeleteNote || 'Sei sicuro di voler eliminare questa nota? Questa azione non può essere annullata.')) {
                        return;
                      }
                      
                      // Call deleteDealNote from the store
                      const success = await deleteDealNote(noteId, id);
                      
                      if (success) {
                        // Refresh notes after deletion
                        loadDealNotes();
                        toast.success(t.statusUpdate?.noteDeleted || 'Nota eliminata correttamente');
                      } else {
                        toast.error(t.statusUpdate?.deleteNoteError || 'Failed to delete note');
                      }
                    } catch (error) {
                      console.error('Error deleting note:', error);
                      toast.error(t.statusUpdate?.deleteNoteError || 'Failed to delete note');
                    }
                  }}
                  onEditActivity={handleEditActivity}
                  onDeleteActivity={async (activityId) => {
                    try {
                      if (!id) return;

                      // Optimistic: remove immediately from UI before network call
                      setCurrentDeal(prev => prev ? {
                        ...prev,
                        activities: (prev.activities || []).filter(a => a.id !== activityId)
                      } : null);

                      const success = await deleteDealActivity(activityId, id);

                      if (success) {
                        toast.success(t.statusUpdate?.activityDeleted || 'Activity deleted successfully');
                        onActivityChanged?.();
                        refresh(); // background consistency refresh
                      } else {
                        refresh(); // rollback optimistic update via refresh
                        toast.error(t.statusUpdate?.deleteActivityError || 'Failed to delete activity');
                      }
                    } catch (error) {
                      console.error('Error deleting activity:', error);
                      refresh(); // rollback optimistic update via refresh
                      toast.error(t.statusUpdate?.deleteActivityError || 'Failed to delete activity');
                    }
                  }}
                  onUpdateActivities={(updatedActivities) => {
                    // Simple update with activities
                    
                    // Use direct state update with memoization to prevent unnecessary renders
                    setCurrentDeal(prev => {
                      if (!prev) return null;
                      
                      // Verifica se le attività sono effettivamente cambiate prima di aggiornare lo stato
                      if (prev.activities && prev.activities.length === updatedActivities.length) {
                        // Verifica veloce basata su id e status
                        const sameActivities = prev.activities.every((prevAct, idx) => {
                          const updatedAct = updatedActivities[idx];
                          return prevAct.id === updatedAct.id && 
                                 prevAct.status === updatedAct.status;
                        });
                        
                        // Se le attività sono essenzialmente le stesse, non aggiornare lo stato
                        if (sameActivities) {
                            return prev;
                        }
                      }
                      
                      // Se siamo qui, le attività sono cambiate
                      onActivityChanged?.();
                      return {
                        ...prev,
                        activities: updatedActivities.map(a => ({...a})) // safe copy
                      };
                    });
                  }}
                />
              </div>
            </div>


            {/* Control for activating/deactivating contract - only for accepted deals */}
            {currentDeal?.status === 'accepted' && (
              <div className="mt-4 md:mt-8 mb-4 md:mb-8">
                <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 rounded-lg md:rounded-xl p-4 md:p-6">
                  <h2 className="text-base md:text-xl font-medium text-black dark:text-white mb-3 md:mb-4">Gestione stato contratto</h2>

                  <DealStatusControl
                    dealId={currentDeal.id}
                    isActive={currentDeal.is_active !== false} // Default to true if undefined
                    onStatusChange={(isActive) => {
                      // Update local state
                      setCurrentDeal(prev => prev ? { ...prev, is_active: isActive } : null);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Deal Contacts Section - mostra contatti deal o fallback a contatti cliente */}
            {((currentDeal?.deal_contacts && currentDeal.deal_contacts.length > 0) ||
              (currentDeal?.client?.contacts && currentDeal.client.contacts.length > 0) ||
              currentDeal?.client?.phone || currentDeal?.client?.whatsapp_number || currentDeal?.client?.email) && (
              <div className="mt-4 md:mt-8">
                <div className="bg-white dark:bg-gray-800/50 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-xl p-4 md:p-6">
                  <DealContactsSection
                    dealContacts={currentDeal.deal_contacts}
                    customerContacts={currentDeal.client?.contacts}
                    clientPhone={currentDeal.client?.phone || currentDeal.client?.whatsapp_number}
                    clientEmail={currentDeal.client?.email}
                  />
                </div>
              </div>
            )}

            {/* Referrers Section */}
            <div className="mt-4 md:mt-8">
              {memoizedDealId && <DealReferrerSection dealId={memoizedDealId} />}
            </div>
            
            {/* FattureInCloud Section - solo se abilitato nelle impostazioni */}
        {currentDeal?.status === 'accepted' && currentDeal?.id &&
         !settings?.disabled_menu_items?.includes('fattureincloud') && (
          <div className="mt-4 md:mt-8">
            <React.Suspense fallback={
              <div className="p-4 border rounded-lg">
                <div className="animate-pulse flex space-x-4">
                  <div className="flex-1 space-y-4 py-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              </div>
            }>
              <ErrorBoundary
                fallback={
                  <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
                    <p className="text-red-500 dark:text-red-400 font-medium">
                      Impossibile caricare i dati di Fatture in Cloud. 
                      <button 
                        onClick={() => window.location.reload()} 
                        className="ml-2 text-blue-500 underline"
                      >
                        Riprova
                      </button>
                    </p>
                  </div>
                }
              >
                <EnhancedFattureInCloudSection dealId={memoizedDealId} translationPrefix={{}} />
              </ErrorBoundary>
            </React.Suspense>
          </div>
        )}
            
            {/* Documenti richiesti sull'offerta: contratti firmati, allegati
                obbligatori. Stesso meccanismo della scheda cliente. */}
            <div className="mt-4 md:mt-8">
              <div className="glass-card rounded-lg p-4 md:p-6">
                <DocumentCollectionsSection aggancio={{ deal_id: currentDeal.id }} />
              </div>
            </div>

            {/* Contract Renewal Section */}
            <div className="mt-4 md:mt-8">
              <div className="glass-card rounded-lg p-4 md:p-6">
                <h2 className="text-base md:text-xl font-medium text-black dark:text-white mb-3 md:mb-4">Rinnovi contratti</h2>

                <ContractRenewalSection
                  deal={currentDeal}
                  onUpdate={refresh}
                />
              </div>
            </div>
            
            {/* Status Update Drawer */}
            {currentDeal && (
              <StatusUpdateDrawer
                isOpen={showStatusUpdateDrawer}
                onClose={() => {
                  setShowStatusUpdateDrawer(false);
                  setEditingActivity(null);
                  setComposerNoteText(null);
                  setComposerActivityData(null);
                }}
                currentStatus={currentDeal.status}
                currentAssignee={currentDeal.assigned_to}
                dealId={currentDeal.id}
                dealLeadId={(currentDeal as any).lead_id}
                dealClientId={currentDeal.client_id}
                defaultTab={statusDrawerDefaultTab}
                editingActivity={editingActivity}
                initialActivityData={composerActivityData}
                initialNoteText={composerNoteText}
                defaultStatusOpen={statusSlotDefaultOpen}
                onStatusUpdated={() => {
                  // Refresh all deal data when status is updated
                  refresh();
                }}
                onNotesUpdated={loadDealNotes}
                onAssigneeUpdated={() => {
                  // Refresh all deal data when assignee is updated
                  refresh();
                }}
                translations={t}
              />
            )}
            
            {/* PDF Viewer Modal for Contract */}
            {currentDeal && currentDeal.signed_contract_url && (
              <PDFViewerModal
                isOpen={showContractViewerModal}
                onClose={() => setShowContractViewerModal(false)}
                pdfUrl={currentDeal.signed_contract_url}
                bucketName={contractBucketName}
                filePath={contractFilePath}
                dealId={currentDeal.id}
                title={t.contract?.viewTitle || `Contract - ${currentDeal.title}`}
                translations={{
                  close: t.contract?.close || 'Close',
                  download: t.contract?.download || 'Download',
                  loading: t.contract?.loading || 'Loading contract...',
                  refresh: t.contract?.refresh || 'Refresh',
                  error: t.contract?.error || 'Error loading document. Try refreshing.'
                }}
              />
            )}

            {/* File Too Large Modal */}
            <ResponsiveModal
              isOpen={showFileTooLargeModal}
              onClose={() => setShowFileTooLargeModal(false)}
              title="File troppo grande"
              size="md"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-center text-amber-500 mb-4">
                  <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-center text-black/70 dark:text-white/70">
                  Il file PDF è troppo grande per essere caricato. Il limite massimo è di 10MB.
                </p>
                <p className="text-center text-black/70 dark:text-white/70">
                  Puoi comprimere il PDF gratuitamente usando <strong>iLovePDF</strong>:
                </p>
                <div className="flex justify-center">
                  <a
                    href="https://www.ilovepdf.com/compress_pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Comprimi PDF con iLovePDF
                  </a>
                </div>
                <p className="text-center text-sm text-black/50 dark:text-white/50">
                  Dopo aver compresso il file, torna qui e riprova il caricamento.
                </p>
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => setShowFileTooLargeModal(false)}
                    className="px-4 py-2 text-sm font-medium text-black dark:text-white glass-card rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </ResponsiveModal>
          </>
        ) : (
          <div className="p-4 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Errore nel caricamento dati</h2>
            <p className="mb-4">Non è stato possibile caricare i dati del deal.</p>
            <button 
              className="px-4 py-2 bg-[#0f6cbd] text-white rounded-md hover:bg-[#0a5ba8]"
              onClick={() => window.location.reload()}
            >
              Riprova
            </button>
          </div>
        )}
      </PageContainer>

      {showShareToChat && currentDeal && (
        <ShareToChatModal
          entityType="deal"
          entityId={currentDeal.id}
          entityData={{
            title: currentDeal.title,
            client_name: currentDeal.client?.business_name || '',
            amount: currentDeal.amount || 0,
            status: currentDeal.status,
            quote_number: currentDeal.quote_number || ''
          }}
          onClose={() => setShowShareToChat(false)}
        />
      )}
    </>
    );
  } catch (error) {
    console.error("Error during rendering:", error);
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Error Rendering Deal Page</h2>
        <p className="text-red-800 dark:text-red-300 mb-4">An unexpected error occurred while rendering the deal details.</p>
        <button 
          className="px-4 py-2 bg-[#0f6cbd] text-white rounded-md hover:bg-[#0a5ba8]"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    );
  }
}